import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ImageGallery from './components/ImageGallery';
import SpecificationCard from './components/SpecificationCard';
import FarmMap from './components/FarmMap';
import SupplierInfo from './components/SupplierInfo';
import TestReports from './components/TestReports';
import RequestOrderForm from './components/RequestOrderForm';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { beansService, adminTestReportsService, adminShippingLogisticsService } from '../../services/supabaseService';
import { useLanguage } from '../../contexts/LanguageContext';

const BeanDetail = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const beanId = searchParams?.get('id');
  const [beanData, setBeanData] = useState(null);
  const [testReports, setTestReports] = useState([]);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBeanData();
  }, [beanId]);

  const loadBeanData = async () => {
    try {
      setLoading(true);
      const [beanResult, reportsResult, shippingResult] = await Promise.all([
        beansService?.getById(beanId),
        adminTestReportsService?.getAll(beanId),
        adminShippingLogisticsService?.getAll(beanId)
      ]);
      setBeanData(beanResult);
      setTestReports(reportsResult);
      setShippingOptions(shippingResult);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px] flex items-center justify-center min-h-[calc(100vh-60px)]">
          <Icon name="Loader2" size={48} className="animate-spin text-primary" />
        </main>
      </div>
    );
  }

  if (error || !beanData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px] flex items-center justify-center min-h-[calc(100vh-60px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
            <p className="text-error">{error || (language === 'en' ? 'Bean not found' : '未找到咖啡豆')}</p>
          </div>
        </main>
      </div>
    );
  }

  const breadcrumbs = [
    { label: { en: 'Home', zh: '首页' }, path: '/home' },
    { label: { en: 'Browse Beans', zh: '浏览咖啡豆' }, path: '/browse-beans' },
    { label: { en: beanData?.nameEn, zh: beanData?.nameZh }, path: null }
  ];

  return (
    <>
      <Helmet>
        <title>{language === 'en' ? beanData?.nameEn : beanData?.nameZh} - Africoffee</title>
        <meta name="description" content={language === 'en' ? beanData?.descriptionEn?.substring(0, 160) : beanData?.descriptionZh?.substring(0, 160)} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px]">
          <FullWidthSection padding="" py="py-8">
            <ContentWrapper padding="px-4 sm:px-6 lg:px-8">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
              {breadcrumbs?.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Icon name="ChevronRight" size={16} className="text-muted-foreground" />}
                  {crumb?.path ? (
                    <Link
                      to={crumb?.path}
                      className="text-muted-foreground hover:text-foreground transition-smooth no-underline"
                    >
                      {crumb?.label?.[language]}
                    </Link>
                  ) : (
                    <span className="text-foreground font-medium">{crumb?.label?.[language]}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Images and Description */}
              <div className="lg:col-span-2 space-y-8">
                <ImageGallery 
                  images={beanData?.images?.map(img => ({ url: img?.imageUrl, alt: language === 'en' ? img?.altTextEn : img?.altTextZh })) || []} 
                  beanName={language === 'en' ? beanData?.nameEn : beanData?.nameZh}
                  language={language}
                />

                {/* Product Header */}
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
                        {language === 'en' ? beanData?.nameEn : beanData?.nameZh}
                      </h1>
                      <div className="flex items-center gap-3 text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="MapPin" size={16} />
                          <span>{language === 'en' ? beanData?.country?.nameEn : beanData?.country?.nameZh}</span>
                        </div>
                        <span>•</span>
                        <span>{beanData?.productionYear}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-heading font-bold text-primary">
                        ${beanData?.priceUsdPerKg}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {language === 'en' ? 'per kg' : '每公斤'}
                      </p>
                    </div>
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <p className="text-foreground">
                      {language === 'en' ? beanData?.descriptionEn : beanData?.descriptionZh}
                    </p>
                  </div>
                </div>

                {/* Specifications */}
                <SpecificationCard 
                  bean={beanData} 
                  language={language}
                  specifications={beanData}
                  farmData={beanData?.farm}
                />

                {/* Farm Map - MOVED HERE UNDER SPECIFICATION */}
                {beanData?.farm?.geojson && (
                  <FarmMap 
                    farm={beanData?.farm} 
                    language={language}
                    farmData={beanData?.farm}
                    country={beanData?.country}
                  />
                )}

                {/* Test Reports */}
                {testReports?.length > 0 && (
                  <TestReports reports={testReports} language={language} />
                )}
              </div>

              {/* Right Column - Supplier Info and Order Form */}
              <div className="space-y-6">
                <SupplierInfo supplier={beanData?.supplier} language={language} />
                <RequestOrderForm 
                  bean={beanData} 
                  language={language}
                  beanId={beanData?.id}
                  beanName={language === 'en' ? beanData?.nameEn : beanData?.nameZh}
                />
              </div>
            </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>
      </div>
    </>
  );
};

export default BeanDetail;